// Bridges the blog and programmatic-landing-page silos, which an earlier
// SEO audit found had 0 links in
// either direction across all 18 landing pages and all sampled blog posts -
// called "the single highest-leverage fix available: the content already
// exists on both sides, it just isn't wired together."
//
// Matches by keyword against a post's title/tags rather than hardcoding
// specific blog slugs, since blog posts are fetched live from an external
// CMS and a hardcoded slug can 404 the moment a post is renamed or removed
// - a keyword match degrades gracefully (no match = no link shown) instead
// of linking to a dead post.
import type { BlogListItem } from "./blogList.util";

interface BridgeRule {
  keywords: string[];
  landingPath: string;
  landingLabel: string;
}

const BRIDGE_RULES: BridgeRule[] = [
  {
    keywords: ["restaurant", "cafe", "dining", "diner"],
    landingPath: "/customer-retention-for-restaurants",
    landingLabel:
      "Turn First-Time Diners Into Regulars With Restaurant Loyalty Rewards",
  },
  {
    keywords: ["whatsapp"],
    landingPath: "/whatsapp-marketing-for-retail-business",
    landingLabel:
      "Send WhatsApp Campaigns That Bring Customers Back to Your Shop",
  },
  {
    keywords: ["cashback"],
    landingPath: "/cashback-and-rewards-system-for-shops",
    landingLabel: "Reward Customers With Cashback Instead of Flat Discounts",
  },
  {
    keywords: ["footfall", "foot traffic", "walk-in", "walk in"],
    landingPath: "/increase-footfall-in-retail-store",
    landingLabel: "Increase Footfall in Your Retail Store with Local Offers",
  },
  {
    keywords: [
      "repeat customer",
      "coming back",
      "come back",
      "not coming back",
    ],
    landingPath: "/how-to-increase-repeat-customers",
    landingLabel: "How to Increase Repeat Customers for Your Retail Business",
  },
  {
    keywords: ["pharmac", "medical store"],
    landingPath: "/loyalty-program-for-retail-stores",
    landingLabel:
      "Design a Loyalty Program With Rules That Actually Fit Your Store",
  },
  {
    keywords: [
      "loyalty program",
      "loyalty software",
      "traditional loyalty",
      "tier-2",
      "tier 2",
      "loyalty trend",
    ],
    landingPath: "/customer-loyalty-platform",
    landingLabel:
      "One Loyalty Platform to Replace Every Tool You're Juggling Today",
  },
];

/** For a blog post: the one landing page most relevant to it, if any. */
export function findLandingPageForPost(
  title: string,
  tags: string[] = [],
): { path: string; label: string } | null {
  const haystack = `${title} ${tags.join(" ")}`.toLowerCase();
  for (const rule of BRIDGE_RULES) {
    if (rule.keywords.some((keyword) => haystack.includes(keyword))) {
      return { path: rule.landingPath, label: rule.landingLabel };
    }
  }
  return null;
}

/** For a landing page: up to `limit` blog posts whose title matches these keywords. */
export function findRelatedBlogPosts(
  keywords: string[],
  blogList: BlogListItem[],
  limit = 3,
): BlogListItem[] {
  const lowerKeywords = keywords.map((k) => k.toLowerCase());
  return blogList
    .filter((post) => {
      const title = (post.title || "").toLowerCase();
      return lowerKeywords.some((keyword) => title.includes(keyword));
    })
    .slice(0, limit);
}
